import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {BindingConfig, BindingIconPlace, BindingTooltipPlace} from '@mintdata/mint-kit/api/properties/BindingConfig';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {AByte} from '@mintdata/mint-kit/api/values/AByte';
import {APositiveNumber} from '@mintdata/mint-kit/api/values/APositiveNumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {ColorConverter} from '@mintdata/mint-kit/impl/properties/converters/ColorConverter';
import {AByteConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AByteConfig';
import {APositiveNumberConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/APositiveNumberConfig';
import {AStringConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AStringConfig';
import {HexColorConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/HexColorConfig';

import * as tinyColor from 'tinycolor2';

declare interface IRGB
{
    red:number;
    green:number;
    blue:number;
}

interface IHSL
{
    hue:number;
    saturation:number;
    lightness:number;
}

export class Color
{
    @Property<AString>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: HexColorConfig,
        typeCtor: AString,
    })
    public hex:AString;

    @Property<APositiveNumber>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: APositiveNumberConfig,
        typeCtor: APositiveNumber,
        binding: BindingConfig.with('@colorAlpha[0]', BindingIconPlace.RightMiddle, BindingTooltipPlace.Left)
    })
    public alpha:APositiveNumber;

    public constructor(hex:AString,
                       alpha:APositiveNumber)
    {
        this.hex = hex;
        this.alpha = alpha;
    }

    public clone():Color
    {
        return new Color(this.hex, this.alpha);
    }

    @Property<AString>({
        title: 'RGBA',
        tip: 'RGBA',
        isRequired: true,
        typeConfig: AStringConfig,
        typeCtor: AString,
    })
    public get rgba():AString
    {
        const result:string = this.toHexString();

        return result ? AString.fromString(result) : null;
    }

    public set rgba(rgba:AString)
    {
        if( rgba && ColorConverter.HEXA_COLOR_VALIDATION_REGEXP.test(rgba.value) )
        {
            const isShort = rgba.value.length === 5;
            const lengthAndFrom:number = isShort ? 4 : 7;

            const hex:string = rgba.value.substr(0, lengthAndFrom);
            let alpha:string = rgba.value.substr(lengthAndFrom, 2);

            if( isShort ) alpha += alpha;

            this.hex = AString.fromString(hex);
            this.alpha = APositiveNumber.fromNumber(parseInt(alpha, 16) / 255);
        }
        else
        {
            this.hex = AString.fromString('Invalid HEX Value');
            this.alpha = APositiveNumber.fromNumber(0);
        }
    }

    @Property<AByte>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: AByteConfig,
        typeCtor: AByte,
    })
    public get red():AByte
    {
        if( this.hex != null && this.hex.value != null )
        {
            const rgb:IRGB = Color._RGBFromHex(this.hex.value);
            return rgb ? AByte.fromNumber(rgb.red) : AByte.fromNumber(0);
        }

        return null;
    }

    public set red(red:AByte)
    {
        if( this.hex != null && this.hex.value != null && red != null && red.value != null )
        {
            const rgb:IRGB = Color._RGBFromHex(this.hex.value);

            rgb.red = red.value;

            const hexString:string = Color._hexFromRGB(rgb);

            this.hex = AString.fromString(hexString);
        }
    }

    @Property<AByte>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: AByteConfig,
        typeCtor: AByte,
    })
    public get green():AByte
    {
        if( this.hex != null && this.hex.value != null )
        {
            const rgb:IRGB = Color._RGBFromHex(this.hex.value);
            return rgb ? AByte.fromNumber(rgb.green) : AByte.fromNumber(0);
        }

        return null;
    }

    public set green(green:AByte)
    {
        if( this.hex != null && this.hex.value != null && green != null && green.value != null )
        {
            const rgb:IRGB = Color._RGBFromHex(this.hex.value);

            rgb.green = green.value;

            const hexString:string = Color._hexFromRGB(rgb);

            this.hex = AString.fromString(hexString);
        }
    }

    @Property<AByte>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: AByteConfig,
        typeCtor: AByte,
    })
    public get blue():AByte
    {
        if( this.hex != null && this.hex.value != null )
        {
            const rgb:IRGB = Color._RGBFromHex(this.hex.value);
            return rgb ? AByte.fromNumber(rgb.blue) : AByte.fromNumber(0);
        }

        return null;
    }

    public set blue(blue:AByte)
    {
        if( this.hex != null && this.hex.value != null && blue != null && blue.value != null )
        {
            const rgb:IRGB = Color._RGBFromHex(this.hex.value);

            rgb.blue = blue.value;

            const hexString:string = Color._hexFromRGB(rgb);

            this.hex = AString.fromString(hexString);
        }
    }

    public toHexString():string
    {
        return this.hex && this.hex.value != null && this.alpha && this.alpha.value != null
            ? `${this.hex.value}${Color._padZero(Math.round(this.alpha.value * 255).toString(16))}`
            : null;
    }

    public get isValid():boolean
    {
        return this.hex && this.alpha &&
            !this.hex.isError && !this.alpha.isError &&
            !!Color._RGBFromHex(this.hex.value);
        // HexColorConverter.HEX_COLOR_VALIDATION_REGEXP.test(this.hex.value);
    }

    public toCssString():string
    {
        if( !this.isValid )
            return null;

        // if( this.hex.value.length === 5 || this.hex.value.length === 9 )
        // {
        //     return this.hex.value;
        // }

        const rgb = Color._RGBFromHex(this.hex.value);

        return rgb ? `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${this.alpha})` : null;
    }

    public toRGBAArray():Array<Number>
    {
        return [this.red.value, this.green.value, this.blue.value, Math.floor(this.alpha.value * 255)];
    }

    public toHSLString():string
    {
        const hsl:IHSL = Color._rgbToHsl({red: this.red.value, green: this.green.value, blue: this.blue.value});
        const h = Math.round(hsl.hue * 360);
        const s = Math.round(hsl.saturation * 100);
        const l = Math.round(hsl.lightness * 100);

        return this.alpha.value === 1
            ? `hsl(${h}, ${s}%, ${l}%)`
            : `hsla(${h}, ${s}%, ${l}%, ${this.alpha.value})`;
    }

    public toString():string
    {
        return this.toCssString();
    }

    public static fromString(value:string):Color
    {
        const color = tinyColor(value);

        if( !color.isValid() )
            return null;

        return new Color(
            AString.fromString(color.toHexString()),
            APositiveNumber.fromNumber(color.getAlpha()),
        );
    }

    public static fromHexString(hexString:string):Color
    {
        return this.fromString(hexString);
    }

    public static fromHexAlpha(hex:string, alpha:number):Color
    {
        return new Color(AString.fromString(hex), APositiveNumber.fromNumber(parseFloat(alpha.toFixed(2))));
    }

    public static fromRGBA(red:number,
                           green:number,
                           blue:number,
                           alpha:number):Color
    {
        const hexString:string = Color._hexFromRGB({red, green, blue});

        return hexString && new Color(
            AString.fromString(hexString),
            APositiveNumber.fromNumber(parseFloat(alpha.toFixed(2))),
        );
    }

    public static transparent():Color
    {
        return Color.fromRGBA(0, 0, 0, 0.0);
    }

    public static black():Color
    {
        return Color.fromRGBA(0, 0, 0, 1.0);
    }

    public static white():Color
    {
        return Color.fromRGBA(255, 255, 255, 1.0);
    }

    public static awesome():Array<Color>
    {
        return [
            Color.fromRGBA(51, 102, 204, 1),
            Color.fromRGBA(220, 57, 18, 1),
            Color.fromRGBA(255, 153, 0, 1),
            Color.fromRGBA(16, 150, 24, 1),
            Color.fromRGBA(153, 0, 153, 1),
            Color.fromRGBA(59, 62, 172, 1),
            Color.fromRGBA(0, 153, 198, 1),
            Color.fromRGBA(221, 68, 119, 1),
            Color.fromRGBA(102, 170, 0, 1),
            Color.fromRGBA(184, 46, 46, 1),
            Color.fromRGBA(49, 99, 149, 1),
            Color.fromRGBA(153, 68, 153, 1),
            Color.fromRGBA(34, 170, 153, 1),
            Color.fromRGBA(170, 170, 17, 1),
            Color.fromRGBA(102, 51, 204, 1),
            Color.fromRGBA(230, 115, 0, 1),
            Color.fromRGBA(139, 7, 7, 1),
            Color.fromRGBA(50, 146, 98, 1),
            Color.fromRGBA(85, 116, 166, 1),
            Color.fromRGBA(59, 62, 172, 1),
        ];
    }

    public static exampleFrame():IFrame
    {
        return exampleFrame;
    }

    public static lighten(color:Color, amount:number = 10)
    {
        const hsl:IHSL = Color._rgbToHsl({red: color.red.value, blue: color.blue.value, green: color.green.value});
        hsl.lightness += amount / 100;
        hsl.lightness = Math.min(1, Math.max(0, hsl.lightness));

        const lighterRGB:IRGB = Color._hslToRgb(hsl);

        return Color.fromRGBA(Math.round(lighterRGB.red), Math.round(lighterRGB.green), Math.round(lighterRGB.blue), color.alpha.value);
    }

    public static darken(color:Color, amount:number = 10)
    {
        const hsl:IHSL = Color._rgbToHsl({red: color.red.value, blue: color.blue.value, green: color.green.value});
        hsl.lightness -= amount / 100;
        hsl.lightness = Math.min(1, Math.max(0, hsl.lightness));

        const darkerRGB:IRGB = Color._hslToRgb(hsl);

        return Color.fromRGBA(Math.round(darkerRGB.red), Math.round(darkerRGB.green), Math.round(darkerRGB.blue), color.alpha.value);
    }

    private static _checkRGBRange(value:number):boolean
    {
        return value != null && value >= 0 && value <= 255;
    }

    private static _padZero(value:string):string
    {
        return value && value.length === 1 ? `0${value}` : value;
    }

    private static _RGBFromHex(hex:string):IRGB
    {
        if( !hex )
            return null;

        if( hex.charAt(0) === '#' )
            hex = hex.substring(1);

        let red:number;
        let green:number;
        let blue:number;

        if( hex.length === 3 )
        {
            //#xyz colors are interpreted in CSS as #xxyyzz
            red = parseInt(hex.substring(0, 1) + hex.substring(0, 1), 16);
            green = parseInt(hex.substring(1, 2) + hex.substring(1, 2), 16);
            blue = parseInt(hex.substring(2, 3) + hex.substring(2, 3), 16);
        }
        else if( hex.length === 6 )
        {
            red = parseInt(hex.substring(0, 2), 16);
            green = parseInt(hex.substring(2, 4), 16);
            blue = parseInt(hex.substring(4, 6), 16);
        }

        if( !Color._checkRGBRange(red) || !Color._checkRGBRange(green) || !Color._checkRGBRange(blue) )
            return null;

        return {red, green, blue};
    }

    public static RGBFromHex(hex:string):IRGB
    {
        return Color._RGBFromHex(hex);
    }

    private static _hexFromRGB(rgb:IRGB):string
    {
        if( !rgb || !Color._checkRGBRange(rgb.red) || !Color._checkRGBRange(rgb.green) || !Color._checkRGBRange(rgb.blue) )
            return null;

        return `#${Color._padZero(rgb.red.toString(16))}${Color._padZero(rgb.green.toString(16))}${Color._padZero(rgb.blue.toString(16))}`;
    }

    private static _rgbToHsl(rgb:IRGB):IHSL
    {
        const r:number = rgb.red / 255,
            g:number = rgb.green / 255,
            b:number = rgb.blue / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);

        let h:number,
            s:number,
            l:number = (max + min) / 2;

        if( max == min )
        {
            h = s = 0; // achromatic
        }
        else
        {
            const d:number = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch( max )
            {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }

            h /= 6;
        }

        return {hue: h, saturation: s, lightness: l};
    }

    private static _hslToRgb(hsl:IHSL):IRGB
    {
        let r:number,
            g:number,
            b:number;
        const h:number = hsl.hue,
            s:number = hsl.saturation,
            l:number = hsl.lightness;

        function hue2rgb(p:number, q:number, t:number):number
        {
            if( t < 0 ) t += 1;
            if( t > 1 ) t -= 1;

            if( t < 1 / 6 )
                return p + (q - p) * 6 * t;
            if( t < 1 / 2 )
                return q;
            if( t < 2 / 3 )
                return p + (q - p) * (2 / 3 - t) * 6;

            return p;
        }

        if( s === 0 )
        {
            r = g = b = l; // achromatic
        }
        else
        {
            const q:number = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p:number = 2 * l - q;

            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        return {red: r * 255, green: g * 255, blue: b * 255};
    }
}

const exampleFrame:IFrame = new AFrame(
    [
        new FrameField('fontColorHex', Type.String),
        new FrameField('fontColorAlpha', Type.Number),
    ],
    [
        [
            Color.black().hex,
            Color.black().alpha,
        ],
    ],
);
